import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Plus, Search, Edit, Archive, ArchiveRestore, Trash2, Users, X, RefreshCw } from 'lucide-react';
import ClientForm from './ClientForm';
import SearchCounter from './SearchCounter';
import BlacklistAlert from './BlacklistAlert';
import { useAuth } from '../contexts/AuthContext';
import { getBulkClientBlacklistStatusFromCache } from '../lib/blacklistService';
import { getAllClients, getClientsInUse, searchClientsLocal, refreshClientsData } from '../lib/clientService';
import { matchesSearchTerm } from '../lib/searchUtils';

const ClientsListTab = () => {
  const { userRole, showCacheRefreshButtons } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientsInUse, setClientsInUse] = useState(new Set());
  const [refreshing, setRefreshing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchClients();
  }, [showArchived]);

  // Removed aggressive window focus refresh to prevent reloading on tab switch

  const fetchClients = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      
      // Use local-first client service
      let clientsList = await getAllClients(showArchived, forceRefresh);
      
      // Load blacklist status from IndexedDB cache (no Firestore queries)
      try {
        const clientIds = clientsList.map(client => client.id);
        const blacklistStatus = await getBulkClientBlacklistStatusFromCache(clientIds);
        
        clientsList = clientsList.map(client => ({
          ...client,
          isBlacklisted: blacklistStatus[client.id]?.isBlacklisted || false,
          activeIncidentsCount: blacklistStatus[client.id]?.activeIncidentsCount || 0,
          mostRecentIncidentMotivo: blacklistStatus[client.id]?.mostRecentIncidentMotivo || null
        }));
        
      } catch (error) {
        console.error('Error loading cached blacklist status:', error);
      }
      
      setClients(clientsList);
      
      // Check which clients are used in orders (optimized)
      await checkClientsUsage(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
      console.log('Clients collection may not exist yet, starting with empty list');
      setClients([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const checkClientsUsage = async (clientsList) => {
    try {
      // Use optimized batch usage check instead of individual queries
      const clientIds = clientsList.map(client => client.id);
      const inUseSet = await getClientsInUse(clientIds);
      setClientsInUse(inUseSet);
    } catch (error) {
      console.error('Error checking clients usage:', error);
    }
  };

  const getPrincipalContact = (contactos) => {
    if (!contactos || contactos.length === 0) return null;
    return contactos.find(c => c.esPrincipal) || contactos[0];
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const getTipoClienteText = (tipo) => {
    switch (tipo) {
      case 'particular':
        return 'Particular';
      case 'empresa':
        return 'Empresa';
      default:
        return tipo || 'N/A';
    }
  };

  const getTipoClienteColor = (tipo) => {
    switch (tipo) {
      case 'particular':
        return 'text-blue-600';
      case 'empresa':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getClientDisplayName = (client) => {
    const principal = getPrincipalContact(client.contactos);
    return client.nombreEmpresa || principal?.nombre || 'Cliente';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const handleRefreshCache = async () => {
    try {
      setRefreshing(true);
      const clientsList = await refreshClientsData(showArchived);
      setClients(clientsList);
      
      // Re-check clients usage after refresh
      await checkClientsUsage(clientsList);
      
      setSuccess('Cache de clientes actualizado exitosamente');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error refreshing clients cache:', error);
      setError('Error al actualizar el cache de clientes');
      setTimeout(() => setError(''), 5000);
    } finally {
      setRefreshing(false);
    }
  };

  const handleFormSuccess = () => {
    const message = editingClient ? 'Cliente actualizado exitosamente' : 'Cliente creado exitosamente';
    setSuccess(message);
    setEditingClient(null);
    fetchClients();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClient(null);
  };

  const handleArchive = async (client) => {
    if (!window.confirm(`¿Está seguro de archivar a ${getClientDisplayName(client)}?`)) return;

    try {
      await updateDoc(doc(db, 'clients', client.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${getClientDisplayName(client)} ha sido archivado`);
      fetchClients();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving client:', error);
      setError('Error al archivar el cliente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnarchive = async (client) => {
    if (!window.confirm(`¿Está seguro de desarchivar a ${getClientDisplayName(client)}?`)) return;

    try {
      await updateDoc(doc(db, 'clients', client.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${getClientDisplayName(client)} ha sido desarchivado`);
      fetchClients();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving client:', error);
      setError('Error al desarchivar el cliente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (client) => {
    try {
      const isInUse = await checkClientInOrders(client.id);
      
      if (isInUse) {
        setError(`No se puede eliminar a ${getClientDisplayName(client)} porque tiene órdenes de trabajo asociadas. Debe archivar el cliente en su lugar.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente a ${getClientDisplayName(client)}?\\n\\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar el cliente en su lugar.`
      )) {
        return;
      }

      await deleteDoc(doc(db, 'clients', client.id));
      setSuccess(`${getClientDisplayName(client)} ha sido eliminado permanentemente`);
      fetchClients();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Error al eliminar el cliente');
      setTimeout(() => setError(''), 5000);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    
    const clientFields = [
      client.nombreEmpresa,
      getTipoClienteText(client.tipoCliente)
    ].filter(Boolean);
    
    const clientMatches = clientFields.some(field => 
      field && matchesSearchTerm(field, searchTerm)
    );
    
    const contactMatches = (client.contactos || []).some(contact => {
      const contactFields = [
        contact.nombre,
        contact.telefono,
        contact.email,
        contact.nota
      ].filter(Boolean);
      
      return contactFields.some(field => 
        field && matchesSearchTerm(field, searchTerm)
      );
    });
    
    return clientMatches || contactMatches;
  });

  // Pagination calculations
  const totalItems = filteredClients.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredClients.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showArchived]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Clientes</h2>
        </div>
        <div className="flex items-center space-x-2">
          {showCacheRefreshButtons && (
            <button 
              onClick={handleRefreshCache}
              disabled={refreshing}
              className="flex items-center justify-center w-10 h-10 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Actualizar Cache"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {canEdit() && (
            <button 
              onClick={() => setShowForm(true)}
              className="flex items-center justify-center w-10 h-10 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
              title="Nuevo Cliente"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="mb-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-4">
            {success}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Buscar clientes por empresa, contacto, teléfono, email o notas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <SearchCounter 
              count={filteredClients.length} 
              label="clientes" 
              isSearching={!!searchTerm} 
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showArchived"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 text-green-600 dark:text-green-400 focus:ring-green-500 dark:focus:ring-green-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
            <label htmlFor="showArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
              Mostrar archivados
            </label>
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando clientes...
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron clientes que coincidan con la búsqueda' : 'No hay clientes registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Empresa/Contacto Principal
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Teléfono
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Email
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Tipo
                  </th>
                  {canEdit() && (
                    <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {currentItems.map((client) => {
                  const principal = getPrincipalContact(client.contactos);
                  const displayName = client.nombreEmpresa || principal?.nombre || 'Sin nombre';
                  const displaySubtitle = client.nombreEmpresa ? principal?.nombre : null;
                  
                  return (
                    <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => window.location.href = `/clients/${client.id}`}>
                      <td className="px-2 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <BlacklistAlert client={client} size="sm" />
                            <div className="truncate font-semibold text-gray-900 dark:text-white" title={displayName}>
                              {displayName}
                            </div>
                          </div>
                          {displaySubtitle && (
                            <div className="text-sm text-gray-600 dark:text-gray-300 truncate" title={displaySubtitle}>
                              {displaySubtitle}
                            </div>
                          )}
                          {client.isArchived && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 mt-1">
                              Archivado
                            </span>
                          )}
                          {/* Show additional info on mobile */}
                          <div className="sm:hidden mt-2 space-y-1">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium text-gray-600 dark:text-gray-400">Tel:</span> <span className="text-gray-500 dark:text-gray-400">{principal?.telefono || 'N/A'}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium text-gray-600 dark:text-gray-400">Tipo:</span> <span className={getTipoClienteColor(client.tipoCliente)}>{getTipoClienteText(client.tipoCliente)}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium text-gray-600 dark:text-gray-400">Registro:</span> <span className="text-gray-500 dark:text-gray-400">{formatDate(client.createdAt)}</span>
                            </div>
                            {client.contactos && client.contactos.length > 1 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Contactos adicionales:</span> <span className="text-gray-500 dark:text-gray-400">{client.contactos.length - 1} más</span>
                              </div>
                            )}
                            {principal?.email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Email:</span> <span className="text-gray-500 dark:text-gray-400">{principal.email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white hidden sm:table-cell">
                        {principal?.telefono || 'N/A'}
                      </td>
                      <td className="px-2 py-4 text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                        <div className="max-w-xs">
                          <div className="text-sm leading-tight break-words text-gray-900 dark:text-white" title={principal?.email}>
                            {principal?.email || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-4 text-sm hidden md:table-cell">
                        <span className={`font-medium ${getTipoClienteColor(client.tipoCliente)}`}>
                          {getTipoClienteText(client.tipoCliente)}
                        </span>
                      </td>
                      {canEdit() && (
                        <td className="px-1 py-4 text-sm font-medium">
                          <div className="flex flex-col sm:flex-row sm:space-x-1 space-y-1 sm:space-y-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleEdit(client)}
                              className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                              title="Editar cliente"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {client.isArchived ? (
                              <button
                                onClick={() => handleUnarchive(client)}
                                className="p-1 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                                title="Desarchivar cliente"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleArchive(client)}
                                className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded"
                                title="Archivar cliente"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete() && (
                              <button
                                onClick={() => !clientsInUse.has(client.id) && handleDelete(client)}
                                className={`p-1 rounded ${
                                  clientsInUse.has(client.id)
                                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
                                }`}
                                title={
                                  clientsInUse.has(client.id)
                                    ? 'No se puede eliminar: cliente usado en órdenes de trabajo'
                                    : 'Eliminar cliente'
                                }
                                disabled={clientsInUse.has(client.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            {/* Mobile pagination */}
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>

            {/* Desktop pagination */}
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                  <span className="font-medium">{Math.min(endIndex, totalItems)}</span> de{' '}
                  <span className="font-medium">{totalItems}</span> resultados
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Anterior</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {(() => {
                    const pageNumbers = [];
                    const showEllipsis = totalPages > 7;
                    
                    if (showEllipsis) {
                      if (currentPage <= 3) {
                        for (let i = 1; i <= Math.min(5, totalPages); i++) {
                          pageNumbers.push(i);
                        }
                        if (totalPages > 5) {
                          pageNumbers.push('...');
                          pageNumbers.push(totalPages);
                        }
                      } else if (currentPage >= totalPages - 2) {
                        pageNumbers.push(1);
                        pageNumbers.push('...');
                        for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
                          if (i > 1) pageNumbers.push(i);
                        }
                      } else {
                        pageNumbers.push(1);
                        pageNumbers.push('...');
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                          pageNumbers.push(i);
                        }
                        pageNumbers.push('...');
                        pageNumbers.push(totalPages);
                      }
                    } else {
                      for (let i = 1; i <= totalPages; i++) {
                        pageNumbers.push(i);
                      }
                    }
                    
                    return pageNumbers.map((number, index) => (
                      number === '...' ? (
                        <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                          ...
                        </span>
                      ) : (
                        <button
                          key={number}
                          onClick={() => handlePageChange(number)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                            currentPage === number
                              ? 'z-10 bg-blue-600 dark:bg-blue-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400'
                              : 'text-gray-900 dark:text-gray-300'
                          }`}
                        >
                          {number}
                        </button>
                      )
                    ));
                  })()}

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Siguiente</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <ClientForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingClient={editingClient}
      />
    </div>
  );
};

export default ClientsListTab;